/**
 * Pure LoR (Level of Representation) controller — no React, no Three.js imports.
 * RFC-114 Phase 1: LoR0–LoR3 (Globe → City → Site → Building).
 * Tested in isolation via __tests__/lorController.test.ts.
 */

import type { SceneProfileId, VisualPurpose } from '@/sdk-stub/viewer-sdk/types';
import { allowedVisualPurposes, normalizeSceneProfile } from '@/sdk-stub/viewer-sdk/policy/sceneProfilePolicy';

export type LoRLevel = 0 | 1 | 2 | 3;

export interface LoRState {
  level: LoRLevel;
  /** Human-readable scope name (e.g. "Tokyo"). */
  label: string;
  /** e.g. ["Earth", "Tokyo", "Shinjuku"] */
  breadcrumb: string[];
  activeProfile: SceneProfileId;
  allowedPurposes: readonly VisualPurpose[];
}

/**
 * Data-driven scope labels for the Tier-1 breadcrumb (INIT-2026-Q3-SCENE-HIERARCHY,
 * Block 1.5 / audit-finding D4). Sourced from the loaded scene/gTwin metadata so
 * the breadcrumb reflects the ACTUAL dataset rather than a hardcoded locale.
 *
 * All fields optional — absent fields fall back to neutral generic labels
 * ("Region" / "Site" / "Asset"), NEVER a hardcoded place name.
 */
export interface LoRScopeLabels {
  /** LoR1 label — region / city (e.g. "Tokyo", or the project name). */
  region?: string;
  /** LoR2 label — site / district (e.g. "Shinjuku"). */
  site?: string;
  /** LoR3 label — the asset, used when no specific object is selected. */
  asset?: string;
}

/** Globe root label — the one genuinely universal Tier-1 label. */
export const GLOBE_ROOT_LABEL = 'Earth';

/**
 * Detect LoR level from camera-to-target distance in metres.
 *
 * Thresholds (MULTI_SCALE_LOR_ARCHITECTURE.md §4.3):
 *   LoR0: > 10 000 m   (globe / region)
 *   LoR1: 2 000–10 000 m  (city / district)
 *   LoR2: 250–2 000 m   (site / block)
 *   LoR3: ≤ 250 m       (building exterior detail)
 *
 * Returns LoR0 when distanceM is null (no scene loaded).
 */
export function detectLoRFromDistance(distanceM: number | null): LoRLevel {
  if (distanceM === null) return 0;
  if (distanceM > 10000) return 0;
  if (distanceM > 2000) return 1;
  if (distanceM > 250) return 2;
  return 3;
}

/**
 * Build breadcrumb labels for a given LoR, driven by the dataset's own
 * `scope` labels (Block 1.5 / D4). No hardcoded place names.
 *
 *   LoR0: ["Earth"]
 *   LoR1: ["Earth", <region>]
 *   LoR2: ["Earth", <region>, <site>]
 *   LoR3: ["Earth", <region>, <site>, <selectedBuilding ?? asset>]
 *
 * `scope` is sourced from the loaded scene/gTwin metadata. When a label is
 * absent, a neutral generic fallback is used ("Region" / "Site" / "Asset"),
 * so a brand-new gTwin (Dubai, an oil & gas platform, …) never shows a
 * misleading "Tokyo / Shinjuku".
 */
export function buildBreadcrumb(
  lor: LoRLevel,
  selectedBuilding?: string,
  scope?: LoRScopeLabels,
): string[] {
  const region = scope?.region ?? 'Region';
  const site = scope?.site ?? 'Site';
  const asset = selectedBuilding ?? scope?.asset ?? 'Asset';
  switch (lor) {
    case 0:
      return [GLOBE_ROOT_LABEL];
    case 1:
      return [GLOBE_ROOT_LABEL, region];
    case 2:
      return [GLOBE_ROOT_LABEL, region, site];
    case 3:
      return [GLOBE_ROOT_LABEL, region, site, asset];
  }
}

/**
 * Canonical camera-to-target distances for each LoR level (RFC-114 Phase 2).
 * Used by microscope/telescope buttons to animate to the midpoint of each band.
 */
export const LOR_TARGET_DISTANCES: Record<LoRLevel, number> = {
  0: 15000, // 15 km — globe / region view
  1: 5000,  // 5 km  — city / district
  2: 800,   // 800 m — site / block
  3: 100,   // 100 m — street / building exterior
};

/**
 * Returns the target camera distance when drilling down one LoR level (Microscope).
 * Returns null when already at the deepest level (LoR3).
 */
export function microscopeTargetDistance(currentLoR: LoRLevel): number | null {
  if (currentLoR >= 3) return null;
  return LOR_TARGET_DISTANCES[(currentLoR + 1) as LoRLevel];
}

/**
 * Returns the target camera distance when pulling back one LoR level (Telescope).
 * Returns null when already at the widest level (LoR0).
 */
export function telescopeTargetDistance(currentLoR: LoRLevel): number | null {
  if (currentLoR <= 0) return null;
  return LOR_TARGET_DISTANCES[(currentLoR - 1) as LoRLevel];
}

/**
 * Build full LoRState from distance + optional selected building label + scope.
 * Convenience wrapper combining detectLoRFromDistance + buildBreadcrumb.
 */
export function buildLoRState(
  distanceM: number | null,
  selectedBuilding?: string,
  scope?: LoRScopeLabels,
  sceneProfile?: SceneProfileId,
): LoRState {
  const level = detectLoRFromDistance(distanceM);
  const breadcrumb = buildBreadcrumb(level, selectedBuilding, scope);
  const label = breadcrumb[breadcrumb.length - 1];
  const activeProfile = normalizeSceneProfile(sceneProfile);
  return {
    level,
    label,
    breadcrumb,
    activeProfile,
    allowedPurposes: allowedVisualPurposes(activeProfile, level),
  };
}
